import {Component, DestroyRef, inject, OnInit, signal} from '@angular/core';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {Router} from "@angular/router";
import {combineLatest, timer} from "rxjs";
import {MatCard} from "@angular/material/card";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {MatIcon} from "@angular/material/icon";
import {ContainerComponent} from "@coreui/angular";
import {GameInstanceService} from "../../services/game-instance.service";
import {GameService} from "../../services/game.service";
import {CountdownService} from "../../services/countdown.service";
import {SnackbarService} from "../../services/snackbar.service";
import {AppConfigService} from "../../services/app-config.service";
import {PipesModule} from "../../pipes/pipes.module";
import {UIStateEnum} from "../../enum/UIStateEnum";
import {GameInstanceStatuses} from "../../enum/GameInstanceStatusEnum";
import {GameTypeEnum} from "../../enum/GameTypeEnum";
import {GameThemeType} from "../../enum/GameThemeType";
import {IGameQueryResult} from "../../interfaces/IGameQueryResult";
import {IActiveGameInstance} from "../../interfaces/IActiveGameInstance";

interface IActiveDrawCard {
  game: IGameQueryResult;
  draw: IActiveGameInstance;
}

@Component({
  selector: 'app-cross-game-active-draw-landing',
  standalone: true,
  imports: [ContainerComponent, MatCard, MatIcon, MatProgressSpinner, PipesModule],
  templateUrl: './cross-game-active-draw-landing.component.html',
  styleUrl: './cross-game-active-draw-landing.component.css'
})
export class CrossGameActiveDrawLandingComponent implements OnInit {
  private gameInstanceService = inject(GameInstanceService);
  private gameService = inject(GameService);
  private countdownService = inject(CountdownService);
  private snackBarService = inject(SnackbarService);
  private appConfigService = inject(AppConfigService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  protected readonly UIStateEnum = UIStateEnum;
  protected readonly GameTypeEnum = GameTypeEnum;
  protected readonly GameInstanceStatuses = GameInstanceStatuses;
  protected readonly GameThemeType = GameThemeType;
  protected uiState = signal(UIStateEnum.ShowLoading);
  protected cards = signal<IActiveDrawCard[]>([]);
  protected missingLogos = signal<Set<string>>(new Set());
  private now = signal(new Date());

  ngOnInit(): void {
    combineLatest([this.gameService.selectAllAvailableGames(), this.gameInstanceService.activeDrawsForAllGames()])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ([gamesP, drawsP]) => {
          this.cards.set(gamesP
            .map(gameP => ({game: gameP, draw: drawsP.find(drawP => drawP.GameId === gameP.Id)}))
            .filter((cardP): cardP is IActiveDrawCard => !!cardP.draw)
            .sort((aP, bP) => new Date(aP.draw.EndedOn).getTime() - new Date(bP.draw.EndedOn).getTime()));
          this.uiState.set(UIStateEnum.ShowData);
        },
        error: () => {
          this.uiState.set(UIStateEnum.ShowData);
          this.snackBarService.openErrorSnackBar('There was an error retrieving the active draws. Please try again or contact a system admin');
        }
      });

    timer(0, 1000).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.now.set(new Date()));
  }

  protected countdown(cardP: IActiveDrawCard): string {
    this.now();
    return this.countdownService.formatRemainingTimeUntilDraw(new Date(cardP.draw.EndedOn));
  }

  protected logoUrl(cardP: IActiveDrawCard): string {
    return `${this.appConfigService.cdnRoot()}/assets/${cardP.game.Subdomain}/game-logo.svg`;
  }

  protected logoFailed(cardP: IActiveDrawCard): void {
    this.missingLogos.update(idsP => new Set(idsP).add(cardP.game.Id));
  }

  protected openDraw(cardP: IActiveDrawCard): void {
    this.gameService.updateActiveGame(cardP.game);
    this.router.navigateByUrl(`${cardP.game.Id}/active-game-instances`);
  }
}
